import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { formatInteger } from '@monetize/utils/core';
import isString from 'lodash/isString';
import {
  forwardRef,
  useId,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import MCustomIconButton from '../../MCustomIconButton';
import MCustomRadioGroup from '../../MCustomRadioGroup';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import {
  DataTableFilterRef,
  FilterProps,
  SelectionOption,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

const ALL: SelectionOption = 'ALL';
const SELECTED: SelectionOption = 'SELECTED';
const UNSELECTED: SelectionOption = 'UNSELECTED';

function getOptions(rowCount: number, selectedCount: number) {
  return [
    {
      title: `All (${formatInteger(rowCount)})`,
      value: ALL,
    },
    {
      title: `Selected (${formatInteger(selectedCount)})`,
      value: SELECTED,
    },
    {
      title: `Unselected (${formatInteger(rowCount - selectedCount)})`,
      value: UNSELECTED,
    },
  ];
}

export const DataTableHeaderSelectionFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table, clientControlled }, ref) => {
  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      setSelectedValue('ALL');
    },
    setFilterValue: ({ columnKey, value }) => {
      if (
        columnKey === column.id &&
        isString(value) &&
        [ALL, SELECTED, UNSELECTED].includes(value as SelectionOption)
      ) {
        setSelectedValue(value as SelectionOption);
      }
    },
  }));

  const key = getColumnFilterKey(column);

  const [selectedValue, setSelectedValue] = useState<SelectionOption>(ALL);
  const { open, onOpen, onClose } = useDisclosure();

  const rowCount = table.getCoreRowModel().rows.length;
  const selectedCount = table.getSelectedRowModel().rows.length;

  const options = useMemo(
    () => getOptions(rowCount, selectedCount),
    [rowCount, selectedCount],
  );

  /**
   * Deliberately does NOT depend on `table.options.meta`.
   *
   * `useReactTable` rebuilds `table.options` on every render, and DataTable
   * passes `meta` as an object literal, so `table.options.meta` has a new
   * identity every render. Listing it here made this effect fire on every
   * render, and in client-controlled mode the effect calls
   * `column.setFilterValue`, which always produces a new `columnFilters`
   * array -> re-render -> new `meta` -> effect -> ... an infinite render loop
   * on every client-controlled table with a selection column (Bill Run,
   * Invoice Management, Usage Management, Usage Events).
   *
   * The effect only needs to run when the user picks a different option; it
   * reads `meta` at call time, which does not require it to be a dependency.
   */
  useNonInitialEffect(() => {
    // Always record the filter so the active-filter chips can render; only
    // server-controlled tables act on it to refetch.
    table.options.meta?.onFilterUpdate?.(key, selectedValue);
    if (clientControlled) {
      // For client-controlled, update immediately
      column.setFilterValue(selectedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientControlled, column, key, selectedValue]);

  const handleClose = () => {
    onClose();
  };

  const filtersApplied = selectedValue !== 'ALL';

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter</Popover.Header>
              <Popover.Body minW="304px">
                <MCustomRadioGroup
                  value={selectedValue}
                  my={1}
                  itemTitle="title"
                  itemValue="value"
                  items={options}
                  onChange={(value) =>
                    setSelectedValue(value as SelectionOption)
                  }
                />
              </Popover.Body>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
